import { BaseSyntheticEvent, Dispatch, SetStateAction, SyntheticEvent, useEffect, useState } from "react";

import styles from "./SupportList.module.css";

import { ListViewSchema } from "../common/Common";
import { CategoryType, FAQResType } from "../../screens/SupportScreen";

import ScreenTitle from "../common/ScreenTitle";
import Search, { FAQSearchType, SearchFilters, SearchOptions } from "../common/Search";

import BoardListView from "../common/BoardListView";
import BoardListViewHeader from "../common/BoardListViewHeader";
import BoardListViewItem from "../common/BoardListViewItem";

import reqFaqListData from "../../dummy/process=list";

interface SupportProps {
    categories: CategoryType[];
    changePageMode: (
        asis: string,
        tobe: "main" | "list" | "view",
        opts?: { replace?: boolean; q?: string; type?: number; page?: number; no?: number }
    ) => void;
    changePageType: Dispatch<SetStateAction<number>>;
    changePostNo: Dispatch<SetStateAction<number>>;
    setSearchOptions: Dispatch<SetStateAction<SearchOptions | undefined>>;
    setCurrentPage: Dispatch<SetStateAction<number>>;
    currentPage: number;
    maxPage: number;
    pageType?: number;
    faqList: FAQResType[] | undefined;
    searchedPageType: FAQSearchType;
    setSearchedPageType: Dispatch<SetStateAction<FAQSearchType>>;
}

/**
 * @author khy, smyun
 * @date 2022-08-24 14:11, 2025-05-21
 * @remark 지원문의 화면_글 목록
 * @param SupportProps
 * categories : 키워드 분류(select box) 선택 시 부모 컴포넌트에게 변경되는 분류 전달
 * changePageMode: 화면 종류 변경(메인, 목록, 상세)시 부모컴포넌트에게 변경되는 화면 전달
 * changePageType (required): PageType 변경 시 부모 컴포넌트에게 변경되는 FAQ Type 전달
 * changePostNo (required): PostNo 변경 시 부모 컴포넌트에게 변경되는 페이지 번호 전달
 * setSearchOptions : 검색 옵션 세팅
 * setCurrentPage : 현재 페이지 세팅(페이지네이션)
 * currentPage: 현재 선택된 페이지(페이지네이션)
 * maxPage : 현재 검색어의 최대 페이지(페이지네이션)
 * pageType : 선택된 분류 전달받아 제목에 표출
 * faqList : 전달받은 게시글 내용
 * setSearchedPageType: 검색된 키워드 분류 세팅
 * @returns SupportList
 */

function SupportList(props: SupportProps) {
    const [searchFilters, setSearchFilters] = useState<SearchFilters[]>([]); // <Search> select박스용 키워드 분류

    useEffect(() => {
        if (props.categories.length > 0) {
            const categories = [{ no: "0", name: "전체" }, ...props.categories] as CategoryType[];
            const filters = categories.map(
                (cat: CategoryType) =>
                    ({
                        value: String(cat.no),
                        name: cat.name,
                    } as SearchFilters)
            );
            setSearchFilters(filters);
        }
    }, [props.categories]);

    const faqListSchema: ListViewSchema[] = [
        {
            field: "NO",
            title: "NO",
            widthRatio: 1,
            style: { textAlign: "center" },
        },
        {
            field: "TITLE",
            title: "제목",
            widthRatio: 3,
        },
        {
            field: "AUTHOR",
            title: "작성자",
            widthRatio: 1,
        },
        {
            field: "WRITE_DATE",
            title: "작성일",
            widthRatio: 1,
        },
        {
            field: "COUNT",
            title: "조회수",
            widthRatio: 1,
            style: { textAlign: "center" },
        },
    ];

    const faqListCols = 6;
    const faqListHeader = (
        <>
            <div className={styles.category_name}> {searchFilters.find((filter) => filter.value === props.searchedPageType)?.name ?? "전체"}</div>
            <BoardListViewHeader cols={faqListCols} schema={faqListSchema} />
        </>
    );

    const handleFaqListMouseOver = (event: BaseSyntheticEvent) => {
        event.currentTarget.style.background = "#d0f2ff";
    };

    const handleFaqListMouseOut = (event: BaseSyntheticEvent) => {
        event.currentTarget.style.background = "";
    };

    const handleFaqListClick = (item: any, event: SyntheticEvent) => {
        if (item) {
            props.changePostNo(item.NO);
            // props.changePageMode("list", "view");
            props.changePageMode("list", "view", { replace: false, no: item.NO });
        }
    };
    const handleSearch = (options: SearchOptions | undefined) => {
        props.setSearchOptions(options); // 검색 옵션 세팅
        props.setSearchedPageType(String(props.pageType) as FAQSearchType); // 검색 결과 키워드 제목
        // history에 추가할 pageMode 추가
        props.changePageMode("main", "list", { replace: false, type: props.pageType ?? 0, page: 1, q: options?.keyword ?? "" });
    };

    const handleChange = (value: string) => {
        props.changePageType(Number(value));
    };

    const renderFaqList = (item: object) =>
        props.faqList && props.faqList.length > 0 ? (
            <BoardListViewItem
                schema={faqListSchema}
                cols={faqListCols}
                data={item}
                onMouseOver={handleFaqListMouseOver}
                onMouseOut={handleFaqListMouseOut}
                onClick={handleFaqListClick}
            />
        ) : (
            <div className={styles.no_result}>검색결과가 없습니다</div>
        );

    return (
        <>
            <ScreenTitle title="지원문의" />
            <Search
                label={"키워드 검색"}
                filters={searchFilters}
                doSearch={handleSearch}
                onFilterChange={handleChange}
                selectedFilter={props.searchedPageType}
                type="header2"
            />
            <BoardListView
                header={faqListHeader}
                data={props.faqList}
                renderItem={renderFaqList}
                margin={"60px 10px"}
                pagenation={true}
                currentPageNo={props.currentPage}
                lastPageNo={props.maxPage}
                onPageChanged={(n) => {
                    props.setCurrentPage(n);
                    props.changePageMode("list", "list", { replace: false, page: n }); // 같은 모드라도 URL만 바꿔 push
                }}
            />
        </>
    );
}

SupportList.defaultProps = {
    margin: "0 auto",
    padding: "10px 0px",
    background: "white",
};

export default SupportList;
